<!--
/**
 * SchedulesTable.vue
 * 
 * A generic table component for managing teacher and team schedules in the PTC conference system.
 * This component displays schedules in a tabular format with inline editing capabilities,
 * appointment validation, and bulk operations like adding/removing breaks.
 * 
 * FEATURES:
 * - Generic support for both teachers and teams via scheduleType prop
 * - Inline editing of schedule properties (time interval, seats, room, etc.)
 * - Checkbox-based invitation system for including/excluding schedules
 * - Appointment conflict detection with user confirmation dialogs
 * - Time interval misalignment detection and validation
 * - Bulk break management integration
 * - Search and pagination support
 * - Sorting capabilities
 * 
 * SCHEDULE LIFECYCLE:
 * 1. Load schedules from API based on scheduleType
 * 2. Display in editable table format
 * 3. User modifies schedule properties
 * 4. Validation checks on save (appointments, misalignment)
 * 5. Save to backend with appropriate confirmations
 * 
 * ENTITY TYPES SUPPORTED:
 * - Teachers: Use teacher_uid, teacher_fname, teacher_lname fields
 * - Teams: Use team_uid, team_name fields
 * - Both use unified 'name' field from API responses
 * 
 * VALIDATION TYPES:
 * - Appointment Conflicts: Warns when removing schedules with existing appointments
 * - Time Misalignment: Warns when time interval changes affect existing appointments
 * 
 * @author PTC Development Team
 * @since v3.0
 * @requires Vue 3, Vuetify 3, Pinia
 */
-->
<template>
	<div class="table-overflow-hide">
		<div class="directoryTable scrollable-55vh">
			<table class="table" v-if="eventDay && eventDay.meeting_types">
				<thead class="sticky-header">
					<tr>
						<th class="icon-col">
							<span class="d-flex align-center justify-content-center">
								<v-checkbox
									style="
										max-width: max-content;
										max-height: 24px;
										overflow: hidden;
										display: flex;
										align-items: center;
									"
									class="custom-input"
									density="compact"
									v-model="everyoneInvited"
									@update:model-value="inviteEveryone"
									data-cy="schedule-check-all"
								></v-checkbox>
							</span>
						</th>
						<SortHeader
							title="name"
							:asc="currentSort.asc"
							:sort-key="currentSort.key"
							@click="() => setSort('name')"
							data-cy="report-table-header"
							class="header-cell"
							color="#695b59"
							weight="500"
						></SortHeader>
						<th>Interval</th>
						<th v-if="eventDay.allow_overbooking">Seats</th>
						<th v-if="eventDay.meeting_types.in_person">room</th>
						<th v-if="eventDay.meeting_types.virtual">Virtual Room</th>
						<th v-if="eventDay.meeting_types.phone">Phone</th>
						<th>
							<v-btn
								@click="loadData()"
								icon
								size="x-small"
								:loading="loading"
								style="max-width: 20px; max-height: 20px; float: right"
								color="gray200"
							>
								<v-icon>mdi-reload</v-icon></v-btn
							>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="row in paginatedResults"
						:key="row.uid"
						:class="{
							'table-row': true,
						}"
					>
						<td>
							<v-checkbox
								style="
									max-width: max-content;
									max-height: 24px;
									overflow: hidden;
									display: flex;
									align-items: center;
								"
								class="custom-input"
								density="compact"
								v-model="row.invited"
							></v-checkbox>
						</td>
						<td>{{ row.name }}</td>
						<td>
							<v-text-field
								type="number"
								style="max-width: 80px"
								class="custom-input"
								density="compact"
								:disabled="!row.invited"
								v-model="row.time_interval"
							></v-text-field>
						</td>
						<td v-if="eventDay.allow_overbooking">
							<v-text-field
								type="number"
								style="max-width: 80px"
								class="custom-input"
								density="compact"
								:disabled="!row.invited"
								v-model="row.seats"
								data-cy="schedule-seats"
							></v-text-field>
						</td>
						<td v-if="eventDay.meeting_types.in_person">
							<v-text-field
								style="max-width: 150px"
								class="custom-input"
								density="compact"
								:disabled="!row.invited"
								v-model="row.room"
								v-if="row.meeting_types.in_person"
							></v-text-field>
						</td>
						<td v-if="eventDay.meeting_types.virtual">
							<v-text-field
								style="max-width: 250px"
								class="custom-input"
								density="compact"
								:disabled="!row.invited"
								v-model="row.virtual_room"
								v-if="row.meeting_types.virtual"
							></v-text-field>
						</td>
						<td v-if="eventDay.meeting_types.phone">
							<v-text-field
								style="max-width: 250px"
								class="custom-input"
								density="compact"
								:disabled="!row.invited"
								v-model="row.phone_number"
								v-if="row.meeting_types.phone"
							></v-text-field>
						</td>
						<td class="icon-col">
							<div
								@click="
									{
										state.selected = row;
										state.showModal = true;
									}
								"
								class="d-flex gap-2"
								style="cursor: pointer"
								v-if="row.invited"
								data-cy="view-schedule-btn"
							>
								<span style="white-space: nowrap; color: gray; font-weight: 600"
									>View Schedule</span
								>
								<v-icon color="rgb(120,120,120)">mdi-calendar-text</v-icon>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
			<div
				v-if="loading"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<loading-state text="Loading Schedules"></loading-state>
			</div>
			<div
				v-if="!loading && conference_schedules.length <= 0"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<div class="empty-state">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">We couldn't find any {{ entityTypePlural }}.</div>
					<p>Try reloading the page or adding a new {{ entityTypeLower }}.</p>
				</div>
			</div>
		</div>
	</div>
	<div class="table-pagination-btns">
		<Pagination v-bind="forComponent()"></Pagination>
		<div class="table-buttons">
			<v-btn
				@click="save(false, false)"
				:loading="state.loadingSave"
				data-cy="schedule-save"
				>Save</v-btn
			>
			<v-btn @click="$emit('add:breaks')" data-cy="break-bulk-modal-btn"
				>Add Breaks
				<v-tooltip activator="parent" location="top">
					Create breaks for all {{ entityTypePluralLower }} schedules during a time range
				</v-tooltip>
			</v-btn>
			<v-btn @click="$emit('remove:breaks')"
				>Remove Breaks
				<v-tooltip activator="parent" location="top">
					Remove all breaks for {{ entityTypePluralLower }} schedules during a time range
				</v-tooltip>
			</v-btn>
		</div>
	</div>

	<ScheduleModal
		v-model:show="state.showModal"
		:day-id="eventDay?.conference_days_id || ''"
		:event-day="eventDay || {}"
		:type="props.scheduleType as 'teacher' | 'team'"
		:uid="state.selected?.uid || ''"
		:name="state.selected?.name || ''"
	></ScheduleModal>
	<v-dialog v-model="dialogConfirm" persistent max-width="550">
		<v-card>
			<v-card-title>Attempting to remove schedules that have appointments</v-card-title>
			<v-card-text>
				<div v-for="schedule in schedules_with_meetings" :key="schedule.uid">
					{{ getScheduleName(schedule) }} has
					{{ schedule.num_appointments }}
					{{ schedule.num_appointments > 1 ? "appointments" : "appointment" }} that will
					be deleted.
				</div>
				<div>
					Do you want to continue to remove the schedule for
					{{
						schedules_with_meetings.length > 1
							? `these ${entityTypePluralLower}`
							: `this ${entityTypeLower}`
					}}
					?
				</div>
			</v-card-text>

			<v-card-actions class="space-between">
				<v-btn color="primary" text @click="save(true, false)">Delete</v-btn>
				<v-spacer></v-spacer>
				<v-btn color="primary" text @click="dialogConfirm = false">Cancel</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
	<v-dialog v-model="dialogConfirmIgnoreMisalignedSchedules" persistent max-width="650">
		<v-card>
			<v-card-title
				>Attempting to change time interval for {{ entityTypePluralLower }} with
				schedules</v-card-title
			>
			<v-card-text>
				<div v-for="schedule in schedules_with_meetings" :key="schedule.uid">
					{{ getScheduleName(schedule) }} has {{ schedule.num_appointments }}
					{{ schedule.num_appointments > 1 ? "appointments" : "appointment" }} that could
					be affected.
				</div>
				<div>
					Do you want to ignore the time interval changes for
					{{
						schedules_with_meetings.length > 1
							? `these ${entityTypePluralLower}`
							: `this ${entityTypeLower}`
					}}
					and continue saving?
				</div>
			</v-card-text>

			<v-card-actions class="space-between">
				<v-btn color="primary" text @click="save(true, true)">Proceed</v-btn>
				<v-spacer></v-spacer>
				<v-btn
					color="primary"
					text
					@click="dialogConfirmIgnoreMisalignedSchedules = false"
					>Cancel</v-btn
				>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup lang="ts">
/**
 * IMPORTS AND TYPE DEFINITIONS
 */
import type { DayWithNested } from "@/api_types/school";
import { computed, watch } from "vue";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import Pagination from "@/components/pagination.vue";
import { complexSearch } from "@/utils";
import usePaginate from "@/hooks/usePaginate";
import FetchService from "@/services/fetch.service";
import { reactive, ref } from "vue";
import { useAlertsStore } from "@/stores/alerts";
import ScheduleModal from "./scheduleModal.vue";
import SortHeader from "@/components/global/sortHeader.vue";
import useTableSort from "@/hooks/useTableSort";
import { sort } from "@/utils/sort";
import type { Schedule } from "@/api_types/school";

/**
 * COMPONENT PROPS
 * 
 * @prop {DayWithNested} eventDay - The conference day object containing schedule data
 * @prop {string} search - Search query string for filtering schedules
 * @prop {boolean} loadingEvent - Loading state indicator from parent
 * @prop {string} scheduleType - Type of schedule ('teacher' | 'team')
 */

const props = defineProps<{
	eventDay: DayWithNested;
	search: string;
	loadingEvent: boolean;
	scheduleType: string;
}>();

/**
 * TABLE SORTING CONFIGURATION
 * 
 * Configures sortable columns for the schedule table.
 * Currently supports sorting by name field.
 */
const { currentSort, setSort } = useTableSort([
	{
		key: "name",
		title: "name",
		sortKey: "name",
	},
]);

/**
 * COMPONENT EVENTS
 * 
 * Events emitted to parent component for various actions:
 * - delete:schedule: When a schedule is removed
 * - add:schedule: When a new schedule is added
 * - add:breaks: When bulk break addition is requested
 * - remove:breaks: When bulk break removal is requested
 */

const emit = defineEmits<{
	// (event: "reload"): void;
	(event: "delete:schedule", uid: string): void;
	(
		event: "add:schedule",
		schedule:
			| Partial<DayWithNested["conference_teachers"][number]>
			| Partial<DayWithNested["conference_teams"][number]>,
		scheduleType: "teacher" | "team"
	): void;
	(event: "add:breaks"): void;
	(event: "remove:breaks"): void;
}>();

/**
 * DYNAMIC TEXT COMPUTED PROPERTIES
 * 
 * These computed properties provide dynamic text based on the scheduleType prop,
 * making the component generic for both teachers and teams.
 * 
 * @computed {string} entityType - Capitalized singular form (Teacher/Team)
 * @computed {string} entityTypePlural - Capitalized plural form (Teachers/Teams)
 * @computed {string} entityTypeLower - Lowercase singular form (teacher/team)
 * @computed {string} entityTypePluralLower - Lowercase plural form (teachers/teams)
 */
// Dynamic text computed properties
const entityType = computed(() =>
	props.scheduleType === "teacher" ? "Teacher" : "Team"
);
const entityTypePlural = computed(() =>
	props.scheduleType === "teacher" ? "Teachers" : "Teams"
);
const entityTypeLower = computed(() =>
	props.scheduleType === "teacher" ? "teacher" : "team"
);
const entityTypePluralLower = computed(() =>
	props.scheduleType === "teacher" ? "teachers" : "teams"
);

/**
 * REACTIVE STATE VARIABLES
 * 
 * @var {Ref<boolean>} dialogConfirm - Controls appointment deletion confirmation dialog
 * @var {Ref<boolean>} dialogConfirmIgnoreMisalignedSchedules - Controls time misalignment dialog
 * @var {Ref<ScheduleWithMeetings[]>} schedules_with_meetings - Stores schedules that have appointment conflicts
 * @var {Ref<Schedule>} conference_schedules - Main schedule data array
 * @var {Ref<boolean>} loading - Loading state for data operations
 */
const dialogConfirm = ref(false);
const dialogConfirmIgnoreMisalignedSchedules = ref(false);
const schedules_with_meetings = ref<ScheduleWithMeetings[]>([]);
const conference_schedules = ref([] as Schedule);
const loading = ref(true);

/**
 * COMPONENT INTERNAL STATE
 * 
 * @property {boolean} loadingSave - Loading state for save operations
 * @property {boolean} showModal - Controls the schedule detail modal visibility
 * @property {Schedule[number] | null} selected - Currently selected schedule for viewing
 */
const state = reactive({
	loadingSave: false,
	showModal: false,
	selected: null as null | Schedule[number],
});

/**
 * TYPE DEFINITIONS
 * 
 * ScheduleWithMeetings: Represents a schedule that has existing appointments
 * This type handles both teacher and team schedules with optional fields
 * based on entity type.
 * 
 * @interface ScheduleWithMeetings
 * @property {number} num_appointments - Number of existing appointments
 * @property {string} name - Unified display name from API
 * @property {string} [teacher_fname] - Teacher first name (optional)
 * @property {string} [teacher_lname] - Teacher last name (optional)
 * @property {string} [team_name] - Team name (optional)
 * @property {number} original_time_interval - Original time interval value
 * @property {number} new_time_interval - New/proposed time interval value
 * @property {string} uid - Unique identifier
 */
type ScheduleWithMeetings = {
	num_appointments: number;
	name: string; // Unified name field from API
	teacher_fname?: string; // Optional teacher fields
	teacher_lname?: string; // Optional teacher fields
	team_name?: string; // Optional team field
	original_time_interval: number;
	new_time_interval: number;
	uid: string;
};

/**
 * UTILITY FUNCTIONS
 */

/**
 * Get the display name for a schedule entity
 * 
 * This function handles the different naming conventions between teachers and teams,
 * providing a unified way to display entity names regardless of type.
 * 
 * @param {ScheduleWithMeetings} schedule - The schedule object to get name from
 * @returns {string} The formatted display name
 * 
 * Priority order:
 * 1. Unified 'name' field from API (preferred)
 * 2. Entity-specific fields (teacher_fname + teacher_lname OR team_name)
 * 3. Fallback to generic "Unknown [EntityType]" string
 */
// Helper function to get the display name for a schedule
const getScheduleName = (schedule: ScheduleWithMeetings): string => {
	// First try the unified name field from API
	if (schedule.name) {
		return schedule.name;
	}

	// Fallback to entity-specific fields
	if (
		props.scheduleType === "teacher" &&
		schedule.teacher_fname &&
		schedule.teacher_lname
	) {
		return `${schedule.teacher_fname} ${schedule.teacher_lname}`;
	}

	if (props.scheduleType === "team" && schedule.team_name) {
		return schedule.team_name;
	}

	// Ultimate fallback
	return `Unknown ${entityType.value}`;
};

/**
 * UI INTERACTION HANDLERS
 */

/**
 * Toggle invitation status for all schedules
 * 
 * @param {boolean} checkedValue - Whether to invite everyone or uninvite all
 */

const inviteEveryone = (checkedValue) => {
	console.log(checkedValue);

	filteredSchedules.value.forEach((obj) => {
		obj.invited = checkedValue;
	});
};

/**
 * COMPUTED PROPERTIES
 */

/**
 * Check if everyone is currently invited
 * 
 * @returns {boolean} True if all schedules are invited, false otherwise
 */
const everyoneInvited = computed(() => {
	console.log("everyone invited");
	if (
		!!conference_schedules.value.length &&
		conference_schedules.value.every((i) => i.invited)
	) {
		return true;
	} else {
		return false;
	}
});

/**
 * Filter schedules based on search query
 * 
 * Searches across name, room, phone_number, and uid fields
 * 
 * @returns {Schedule[]} Filtered array of schedules matching search criteria
 */
const filteredSchedules = computed(() =>
	conference_schedules.value.filter((i) =>
		complexSearch(props.search || "", `${i.name} ${i.room} ${i.phone_number} ${i.uid}`)
	)
);

// Pagination hook for table display
const { forComponent, paginatedResults } = usePaginate(filteredSchedules);

/**
 * DATA LOADING FUNCTIONS
 */

/**
 * Load schedule data from API
 * 
 * Fetches schedules for the current conference day and schedule type.
 * Updates the loading state and conference_schedules reactive variable.
 * 
 * @async
 * @function
 */

const loadData = async () => {
	loading.value = true;
	if (props.eventDay?.conference_days_id) {
		const res = await FetchService.fetchData(
			`/conference-days/${props.eventDay.conference_days_id}?schedules=${props.scheduleType}`,
			"get"
		);
		conference_schedules.value = res;
	}
	loading.value = false;
};

/**
 * WATCHERS
 * 
 * Reactive watchers that trigger data reloading when dependencies change.
 */

// Watch props.eventDay
watch(
	() => props.eventDay, // getter function to access the prop to watch
	() => {
		loadData();
	} // function to run when the prop changes
);
watch(
	() => props.scheduleType,
	() => {
		loadData();
	}
);

// load the data when the component loads
loadData();

/**
 * MAIN SAVE FUNCTION
 * 
 * Handles saving schedule changes with validation and error handling.
 * Manages two types of validations:
 * 1. Appointment conflicts (when removing schedules with existing appointments)
 * 2. Time interval misalignment (when changing intervals affects existing appointments)
 * 
 * @param {boolean} confirmed - Whether user confirmed deletion of schedules with appointments
 * @param {boolean} proceed - Whether user confirmed ignoring misalignment warnings
 * 
 * @async
 * @function
 * 
 * WORKFLOW:
 * 1. Handle confirmed deletions (remove schedules from UI)
 * 2. Build request payload with current schedule data
 * 3. Send API request with confirmation flags
 * 4. Handle different response scenarios:
 *    - Success: Show success message, reload data
 *    - Deleted schedules: Show confirmation dialog
 *    - Misaligned schedules: Show misalignment dialog and revert changes
 *    - Error: Show error message
 */

const save = async (confirmed = false, proceed = false) => {
	state.loadingSave = true;
	if (confirmed && !proceed) {
		if (schedules_with_meetings && schedules_with_meetings.value.length > 0) {
			schedules_with_meetings.value.forEach((schedule: ScheduleWithMeetings) => {
				const row = paginatedResults.value.find((r) => r.uid === schedule.uid);
				if (row) {
					row.invited = false;
				}
			});
		}
	}

	if (conference_schedules) {
		console.log(conference_schedules.value);
		//let temp1 = conference_schedules.value.find((t)=> { return t.uid=="7ffc6e55f26ff192501e5bbc163a8340"});
		//console.log(temp1);
		const postBody = {};
		postBody[props.scheduleType] = conference_schedules.value;

		let scheduleType = props.scheduleType == "teacher" ? "teachers" : "teams";
		const res = await FetchService.fetchData(
			`/conference-days/${scheduleType}/${props.eventDay.conference_days_id}?confirmed=${confirmed}&proceed=${proceed}`,
			"post",
			postBody
		);
		if (res?.status) {
			useAlertsStore().addAlert(
				"success",
				`${entityType.value} schedules saved successfully`
			);
			// emit("reload");
			loadData();
			dialogConfirm.value = false;
			dialogConfirmIgnoreMisalignedSchedules.value = false;
		} else {
			if (res?.reason == "deleted schedules") {
				// if there were any schedules that are deleted that had appointments, we need to show a confirmation dialog
				schedules_with_meetings.value = res?.message;
				// show confirmation dialog for deleting schedules
				dialogConfirm.value = true;
				// hide the dialogConfirmIgnoreMisalignedSchedules dialog
				dialogConfirmIgnoreMisalignedSchedules.value = false;
				// schedules_with_meetings.value = res?.message;
				// Define a type for the schedule_with_meetings object

				schedules_with_meetings.value.forEach((schedule: ScheduleWithMeetings) => {
					const row = paginatedResults.value.find((r) => r.uid === schedule.uid);
					if (row) {
						row.invited = true;
					}
				});
			} else if (res?.reason == "misaligned schedules") {
				schedules_with_meetings.value = res?.message;
				dialogConfirm.value = false;
				dialogConfirmIgnoreMisalignedSchedules.value = true;
				// Revert time_interval for affected rows

				console.log("Reverting time intervals...");
				schedules_with_meetings.value.forEach((schedule: ScheduleWithMeetings) => {
					console.log(props.eventDay);

					// Find the row in the current schedules
					const currentRow = conference_schedules.value.find(
						(r) => r.uid === schedule.uid
					);
					if (currentRow) {
						currentRow.time_interval = schedule.original_time_interval;
					}
				});
			} else {
				useAlertsStore().addAlert(
					"error",
					`Failed to save ${entityTypeLower.value} schedules`
				);
				dialogConfirm.value = false;
				dialogConfirmIgnoreMisalignedSchedules.value = false;
			}
		}
	}
	state.loadingSave = false;
};
</script>

<!--
/**
 * COMPONENT STYLES
 * 
 * Scoped SCSS styles for the SchedulesTable component.
 * Uses design system variables and responsive breakpoints.
 * 
 * Key classes:
 * - .icon-col: Minimizes width for icon-only columns
 * - .table-overflow-hide: Handles table overflow behavior
 * - .scrollable-55vh: Creates scrollable container with 55% viewport height
 */
-->
<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;
.icon-col {
	width: 0px;
}
</style>
